import { Field, getIn, useFormikContext } from "formik";
import React, { FC } from "react";
import { useLocale } from "../../contexts/localeContext";
import { TranslationKeys } from "../../contexts/TranslationKeys";
import { MultiSelectOption } from "../../models/MultiSelectOption";
import CheckboxField from "../checkbox-list-field/checkboxField";
import Label from "../label/label";
import SettingSelectField from "../settingFields/SettingSelectField";
import Togglebox from "../togglebox/togglebox";
import { bleNFCKeyValidation, requiredValidation, validateIntegerWithinBounds } from "../../utilities/Validation";

const StidPresetMobileTab: FC = () => {
    const { setFieldValue, errors, values } = useFormikContext<any>();
    const { translate } = useLocale();

    //Used to access the value and errors for deeply nest objects within a Field.
    const siteCodeError = getIn(errors, 'mobileValues.stidMobileValues.siteCode');
    const bleNfeError = getIn(errors, 'mobileValues.stidMobileValues.bleNfcKey');
    const numberOfBytesError = getIn(errors, 'mobileValues.stidMobileValues.numberOfBytes');
    const offsetError = getIn(errors, 'mobileValues.stidMobileValues.offset');

    const cardOptions: MultiSelectOption[] = [
        { value: "0", label: translate.byKey(TranslationKeys.directContact) },
        { value: "1", label: translate.byKeyFormatted(TranslationKeys.upToCentimeters, { value: 20 }) },
        { value: "2", label: translate.byKeyFormatted(TranslationKeys.upToCentimeters, { value: 30 }) },
        { value: "3", label: translate.byKeyFormatted(TranslationKeys.upToCentimeters, { value: 50 }) }
    ];

    const handsFreeOptions: MultiSelectOption[] = [
        { value: "0", label: translate.byKeyFormatted(TranslationKeys.upToMeters, { value: 3 }) },
        { value: "1", label: translate.byKeyFormatted(TranslationKeys.upToMeters, { value: 5 }) },
        { value: "2", label: translate.byKeyFormatted(TranslationKeys.upToMeters, { value: 10 }) },
    ];

    const tapTapOptions: MultiSelectOption[] = [
        { value: "0", label: translate.byKeyFormatted(TranslationKeys.upToMeters, { value: 3 }) },
        { value: "1", label: translate.byKeyFormatted(TranslationKeys.upToMeters, { value: 5 }) },
        { value: "2", label: translate.byKeyFormatted(TranslationKeys.upToMeters, { value: 10 }) },
        { value: "3", label: translate.byKeyFormatted(TranslationKeys.upToMeters, { value: 15 }) },
    ];

    const remoteOptions: MultiSelectOption[] = [
        { value: "0", label: translate.byKeyFormatted(TranslationKeys.upToMeters, { value: 5 }) },
        { value: "1", label: translate.byKeyFormatted(TranslationKeys.upToMeters, { value: 10 }) },
        { value: "2", label: translate.byKeyFormatted(TranslationKeys.upToMeters, { value: 15 }) },
        { value: "3", label: translate.byKeyFormatted(TranslationKeys.upToMeters, { value: 20 }) },
    ];

    const remoteButtonOptions: MultiSelectOption[] = [
        { value: "0", label: translate.byKeyFormatted(TranslationKeys.remoteButton, { value: 1 }) },
        { value: "1", label: translate.byKeyFormatted(TranslationKeys.remoteButton, { value: 2 }) },
    ];

    const siteCodeOptions: MultiSelectOption[] = [
        { value: "1", label: translate.byKey(TranslationKeys.improKeyAndSiteCode) },
        { value: "2", label: translate.byKey(TranslationKeys.customKeyAndSiteCode) },
    ];

    const onSiteOptionChanged = (event: React.ChangeEvent<HTMLInputElement>, siteCodeType: MultiSelectOption) => {
        setFieldValue("mobileValues.stidMobileValues.siteCodeType", siteCodeType.value)
    }

    const onModeChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
        const mode = event.target.value;
        setFieldValue("mobileValues.stidMobileValues.modes", mode);

        if (mode === "standard") {
            setFieldValue("mobileValues.stidMobileValues.handsFree", false);
        }
        else if (mode === "handsFree") {
            setFieldValue("mobileValues.stidMobileValues.handsFree", true);
        }
    }

    return <div className="w-full h-full overflow-y-auto pb-10">
        <div className="flex flex-col py-3">
            <Label
                htmlFor="mobileValues.stidMobileValues.enabled"
                className="text-sm pt-1">{translate.byKey(TranslationKeys.enable)}</Label>
            <Togglebox name="mobileValues.stidMobileValues.enabled" label="" cyName="Enabled" />
        </div>
        <div className="py-2">
            <span className="text-md font-bold">{translate.byKey(TranslationKeys.identificationMode)}</span>
        </div>

        <div className="flex flex-col w-full">
            {/* Standard Mode */}
            <div key={`standardModeRadioButton`} className="flex items-center ">
                <Field
                    id={`standardModeRadioButton`}
                    type="radio"
                    name="mobileValues.stidMobileValues.modes"
                    value={"standard"}
                    disabled={!values.mobileValues.stidMobileValues.enabled}
                    onChange={onModeChanged} />
                <Label htmlFor={`standardModeRadioButton`} className="pl-1 text-sm">{translate.byKey(TranslationKeys.standardModes)}</Label>
            </div>
            <div className="flex flex-col pl-5 w-full">
                {/* Card Mode */}
                <div className="flex flex-row">
                    <div className="w-2/5">
                        <Field
                            id="cardCheckbox"
                            name="mobileValues.stidMobileValues.card">
                            {({ field }) => {
                                return <CheckboxField
                                    compact
                                    disabled={values.mobileValues.stidMobileValues.modes !== "standard" || !values.mobileValues.stidMobileValues.enabled}
                                    option={{ value: "1", label: translate.byKey(TranslationKeys.stidCardMode), isSelected: field.value }}
                                    onSelect={(value, option) => { setFieldValue("mobileValues.stidMobileValues.card", !field.value) }} />
                            }}
                        </Field>
                    </div>
                    <div className="flex flex-row justify-between" id='cardFieldLabel'>
                        <SettingSelectField
                            width="w-64"
                            accessor={`mobileValues.stidMobileValues.cardOption`}
                            disabled={values.mobileValues.stidMobileValues.modes !== "standard" || !values.mobileValues.stidMobileValues.enabled || !values.mobileValues.stidMobileValues.card}
                            options={cardOptions} />
                    </div>
                </div>
                {/* Tap Tap */}
                <div className="flex flex-row">
                    <div className="w-2/5">
                        <Field
                            id="tapTapCheckbox"
                            name="mobileValues.stidMobileValues.tapTap"
                            render={({ field }) => {
                                return <CheckboxField
                                    compact
                                    disabled={values.mobileValues.stidMobileValues.modes !== "standard" || !values.mobileValues.stidMobileValues.enabled}
                                    option={{ value: "1", label: translate.byKey(TranslationKeys.tapTap), isSelected: field.value }}
                                    onSelect={(value, option) => setFieldValue("mobileValues.stidMobileValues.tapTap", !field.value)} />
                            }}
                        />
                    </div>
                    <div className="flex flex-row justify-between" id='tapTapFieldLabel'>
                        <SettingSelectField
                            width="w-64"
                            accessor={`mobileValues.stidMobileValues.tapTapOption`}
                            options={tapTapOptions}
                            disabled={values.mobileValues.stidMobileValues.modes !== "standard" || !values.mobileValues.stidMobileValues.enabled || !values.mobileValues.stidMobileValues.tapTap} />
                    </div>
                </div>
                {/* Remote Mode */}
                <div className="flex flex-row">
                    <div className="w-2/5">
                        <Field
                            id="remoteCheckbox"
                            name="mobileValues.stidMobileValues.remote"
                            render={({ field }) => {
                                return <CheckboxField
                                    compact
                                    disabled={values.mobileValues.stidMobileValues.modes !== "standard" || !values.mobileValues.stidMobileValues.enabled}
                                    option={{ value: "remoteCheckbox", label: translate.byKey(TranslationKeys.remote), isSelected: field.value }}
                                    onSelect={(value, option) => setFieldValue("mobileValues.stidMobileValues.remote", !field.value)} />
                            }}
                        />
                    </div>
                    <>
                        <div className="flex flex-row justify-between" id='remoteFieldLabel'>
                            <SettingSelectField
                                width="w-64"
                                accessor={`mobileValues.stidMobileValues.remoteOption`}
                                options={remoteOptions}
                                disabled={values.mobileValues.stidMobileValues.modes !== "standard" || !values.mobileValues.stidMobileValues.enabled || !values.mobileValues.stidMobileValues.remote} />
                        </div>

                        <div className="flex flex-row space-x-4">
                            {remoteButtonOptions.map((button) => {
                                return <div key={`radioRemoteButton${button.value}`} className="flex items-center ">
                                    <Field
                                        id={`remoteButton${button.value}`}
                                        type="radio"
                                        name="mobileValues.stidMobileValues.remoteButton"
                                        value={button.value}
                                        disabled={values.mobileValues.stidMobileValues.modes !== "standard" || !values.mobileValues.stidMobileValues.enabled || !values.mobileValues.stidMobileValues.remote}
                                        onChange={(_: any) => { setFieldValue("mobileValues.stidMobileValues.remoteButton", button.value) }} />
                                    <Label htmlFor={`remoteButton${button.value}`} className="pl-1 text-sm">{button.label}</Label>
                                </div>
                            })}
                        </div>
                    </>
                </div>
            </div>
            {/* Hands Free Mode */}
            <div key={`handsFreeModeRadioButton`} className="flex flex-col justify-center-center w-full">
                <div className="flex flex-row h-auto items-center">
                    <div className="w-2/5">
                        <Field
                            id={`handsFreeModeRadioButton`}
                            type="radio"
                            name="mobileValues.stidMobileValues.modes"
                            value={"handsFree"}
                            disabled={!values.mobileValues.stidMobileValues.enabled}
                            onChange={onModeChanged} />
                        <Label htmlFor={`handsFreeModeRadioButton`} className="pl-1 text-sm">{translate.byKey(TranslationKeys.handsFree)}</Label>
                    </div>
                    <div className="flex flex-row pl-1 justify-between" id='handsFreeFieldLabel'>
                        <SettingSelectField
                            width="w-64"
                            className="pr-6 ml-2"
                            accessor={`mobileValues.stidMobileValues.handsFreeOption`}
                            disabled={values.mobileValues.stidMobileValues.modes !== "handsFree" || !values.mobileValues.stidMobileValues.enabled}
                            options={handsFreeOptions} />
                    </div>
                </div>
            </div>

            {/* Phone Configuration */}
            <div className="py-2">
                <span className="text-md font-bold">{translate.byKey(TranslationKeys.phoneConfiguration)}</span>
                <Field id="smartUnlock"
                    name="mobileValues.stidMobileValues.smartUnlock"
                    render={({ field }) => {
                        return <CheckboxField
                            compact
                            disabled={!values.mobileValues.stidMobileValues.enabled}
                            option={{ value: "1", label: translate.byKey(TranslationKeys.smartPhoneUnlock), isSelected: field.value }}
                            onSelect={(value, option) => { setFieldValue("mobileValues.stidMobileValues.smartUnlock", !field.value) }} />
                    }} />
            </div>

            {/* Site code */}
            <div className="py-2">
                <span className="text-md font-bold">{translate.byKey(TranslationKeys.siteCode)}</span>
                <div className="flex flex-col space-y-2">
                    {siteCodeOptions.map((siteCodeType) => {
                        return <div key={`improCode${siteCodeType.value}`} className="flex items-center text-sm font-medium ">
                            <Field
                                id={`radioSiteCode${siteCodeType.value}`}
                                type="radio"
                                name="mobileValues.stidMobileValues.siteCodeType"
                                value={siteCodeType.value}
                                disabled={!values.mobileValues.stidMobileValues.enabled}
                                onChange={(e) => onSiteOptionChanged(e, siteCodeType)} />
                            <Label htmlFor={`radioSiteCode${siteCodeType.value}`} className="pl-1 text-sm">{siteCodeType.label}</Label>
                        </div>
                    })}
                </div>
                <div className={`flex flex-col space-y-2 pl-5 ${values.mobileValues.stidMobileValues.siteCodeType === "1" ? 'hidden' : ''}`} >
                    {/* Site Code and BLE/NFC Key */}
                    <div className="flex flex-row space-x-8">
                        <div className="w-64">
                            <div className="flex flex-row justify-between" id="siteCodeLabel">
                                <Label htmlFor="mobileValues.stidMobileValues.siteCode" className="text-xs text-gray-600 mt-1" required>
                                    {translate.byKey(TranslationKeys.siteCodeHex)}
                                </Label>
                                {siteCodeError ? <span className='text-red-600 text-xs'>{siteCodeError}</span> : null}
                            </div>
                            <div className="flex flex-row justify-between">
                                <Field
                                    id="siteCodeId"
                                    type="text"
                                    name="mobileValues.stidMobileValues.siteCode"
                                    className={`flex flex-row items-center p-1 text-gray-800 text-sm w-64 bg-gray-input ${values.mobileValues.stidMobileValues.siteCodeType === "1" ? 'cursor-not-allowed' : ''}`}
                                    disabled={values.mobileValues.stidMobileValues.siteCodeType === "1" || !values.mobileValues.stidMobileValues.enabled}
                                    validate={(value) => requiredValidation(value, translate)} />
                            </div>
                        </div>
                        <div className="w-64">
                            <div className="flex flex-row justify-between" id="bleNfcKeyLabel">
                                <Label htmlFor="mobileValues.stidMobileValues.bleNfcKey" className="text-xs text-gray-600 mt-1" required>
                                    {translate.byKey(TranslationKeys.bleNfcKey)}
                                </Label>
                                {bleNfeError ? <span className='text-red-600 text-xs'>{bleNfeError}</span> : null}
                            </div>
                            <div className="flex flex-row justify-between">
                                <Field
                                    id="bleNfcKeynputId"
                                    type="text"
                                    name="mobileValues.stidMobileValues.bleNfcKey"
                                    disabled={!values.mobileValues.stidMobileValues.enabled}
                                    className="flex flex-row items-center p-1 text-gray-800 text-sm w-64 bg-gray-input"
                                    validate={(value) => bleNFCKeyValidation(value, translate)} />
                            </div>
                        </div>
                    </div>
                    {/* Number of BYTES and Offset */}
                    <div className="flex flex-row space-x-8">
                        <div className="flex flex-col">
                            <div className="flex flex-row justify-between" id="numberOfBytesLabel">
                                <Label
                                    htmlFor="mobileValues.stidMobileValues.numberOfBytes"
                                    className="text-xs text-gray-600 mt-1" required>{translate.byKey(TranslationKeys.numberOfBytes)}:</Label>
                                {numberOfBytesError ? <span className='text-red-600 text-xs'>{numberOfBytesError}</span> : null}
                            </div>
                            <div className="flex flex-row justify-between">
                                <Field
                                    id="numberOfBytes"
                                    type="number"
                                    name="mobileValues.stidMobileValues.numberOfBytes"
                                    disabled={!values.mobileValues.stidMobileValues.enabled}
                                    className="flex flex-row items-center p-1 text-gray-800 text-sm w-64 bg-gray-input"
                                    validate={(value) => validateIntegerWithinBounds(value, 0, 6, translate)} />
                            </div>
                        </div>
                        <div className="flex flex-col">
                            <div className="flex flex-row justify-between" id="offsetLabel">
                                <Label
                                    htmlFor="mobileValues.stidMobileValues.offset"
                                    className="text-xs text-gray-600 mt-1">{translate.byKey(TranslationKeys.offset)}:</Label>
                                {offsetError ? <span className='text-red-600 text-xs'>{offsetError}</span> : null}
                            </div>
                            <div className="flex flex-row justify-between">
                                <Field
                                    id="offset"
                                    type="number"
                                    name="mobileValues.stidMobileValues.offset"
                                    disabled={!values.mobileValues.stidMobileValues.enabled}
                                    className="flex flex-row items-center p-1 text-gray-800 text-sm w-64 bg-gray-input"
                                    validate={(value) => requiredValidation(value, translate)} />
                            </div>
                        </div>
                    </div>
                    {/* Reverse Bytes */}
                    {/* <div className="flex flex-row">
                        <Field
                            id="reverseBytes"
                            name="mobileValues.stidMobileValues.reverseBytes"
                            render={({ field }) => {
                                return <CheckboxField
                                    compact
                                    option={{ value: "1", label: translate.byKey(TranslationKeys.reverseBytes), isSelected: field.value }}
                                    onSelect={(value, option) => { setFieldValue("mobileValues.stidMobileValues.reverseBytes", !field.value); }} />
                            }} />
                    </div> */}
                </div>
            </div>
        </div>
    </div>
}

export default StidPresetMobileTab;
